import React from 'react';
import { Link } from 'wouter';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { FileText, Book } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

// Import shared lecture components
import { 
  KeyTakeaway, 
  Slideshow, 
  SlideContent
} from '@/components/lectures';
import { Separator } from "@/components/ui/separator";

const ThyroidMolecularClinical: React.FC = () => {




  // Lecture slides content
  const lectureSlides: SlideContent[] = [
    {
      title: "Thyroid Hormone Physiology: Molecular Mechanisms",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-4">
              Thyroid hormones T4 (thyroxine) and T3 (triiodothyronine) are synthesized in the thyroid gland's follicular cells under TSH stimulation. Understanding the molecular pathways involved is crucial for effective clinical intervention.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="26" fontWeight="bold" fill="#333">Thyroid Hormone Synthesis Pathway</text>
                
                {/* Step 1: Iodide Uptake */}
                <g transform="translate(60, 100)">
                  <rect x="0" y="0" width="160" height="90" rx="10" fill="#e3f2fd" stroke="#2196f3" strokeWidth="2.5" />
                  <text x="80" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#0d47a1">Iodide Uptake</text>
                  <line x1="30" y1="40" x2="130" y2="40" stroke="#0d47a1" strokeWidth="1.5" />
                  <text x="80" y="65" textAnchor="middle" fontSize="14" fill="#333">
                    <tspan x="80" dy="0">NIS symporter</tspan>
                    <tspan x="80" dy="20">concentrates I⁻</tspan>
                  </text>
                </g>
                
                {/* Step 2: Oxidation & Organification */}
                <g transform="translate(320, 100)">
                  <rect x="0" y="0" width="160" height="90" rx="10" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2.5" />
                  <text x="80" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#1b5e20">Oxidation</text>
                  <line x1="30" y1="40" x2="130" y2="40" stroke="#1b5e20" strokeWidth="1.5" />
                  <text x="80" y="65" textAnchor="middle" fontSize="14" fill="#333">
                    <tspan x="80" dy="0">TPO + H₂O₂</tspan>
                    <tspan x="80" dy="20">oxidizes I⁻ to I</tspan>
                  </text>
                </g>
                
                {/* Step 3: Coupling */}
                <g transform="translate(580, 100)">
                  <rect x="0" y="0" width="160" height="90" rx="10" fill="#fff3e0" stroke="#ff9800" strokeWidth="2.5" />
                  <text x="80" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#e65100">Coupling</text>
                  <line x1="30" y1="40" x2="130" y2="40" stroke="#e65100" strokeWidth="1.5" />
                  <text x="80" y="65" textAnchor="middle" fontSize="14" fill="#333">
                    <tspan x="80" dy="0">TPO couples MIT+DIT</tspan>
                    <tspan x="80" dy="20">to form T3/T4</tspan>
                  </text>
                </g>
                
                {/* Connecting arrows */}
                <path d="M220,145 L320,145" fill="none" stroke="#666" strokeWidth="2" markerEnd="url(#arrowhead)" />
                <path d="M480,145 L580,145" fill="none" stroke="#666" strokeWidth="2" markerEnd="url(#arrowhead)" />
                
                {/* Arrowhead marker definition */}
                <defs>
                  <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#666" />
                  </marker>
                </defs>
                
                {/* T4 vs T3 Box */}
                <g transform="translate(200, 240)">
                  <rect x="0" y="0" width="400" height="150" rx="10" fill="white" stroke="#333" strokeWidth="2" />
                  <text x="200" y="30" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333">Hormone Production</text>
                  <line x1="50" y1="45" x2="350" y2="45" stroke="#333" strokeWidth="1.5" />
                  
                  {/* T4 structure simplified */}
                  <g transform="translate(50, 80)">
                    <text x="0" y="0" fontSize="18" fontWeight="bold" fill="#0d47a1">T4 (Thyroxine)</text>
                    <text x="0" y="25" fontSize="14" fill="#333">• 80% of hormone produced</text>
                    <text x="0" y="50" fontSize="14" fill="#333">• Mostly inactive</text>
                  </g>
                  
                  {/* T3 structure simplified */}
                  <g transform="translate(250, 80)">
                    <text x="0" y="0" fontSize="18" fontWeight="bold" fill="#e65100">T3 (Triiodothyronine)</text>
                    <text x="0" y="25" fontSize="14" fill="#333">• 20% of hormone produced</text>
                    <text x="0" y="50" fontSize="14" fill="#333">• Biologically active form</text>
                  </g>
                </g>
                
                {/* Critical Components Box */}
                <g transform="translate(200, 420)">
                  <rect x="0" y="0" width="400" height="100" rx="10" fill="#f0f0f0" stroke="#555" strokeWidth="2" />
                  <text x="200" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#333">Critical Components</text>
                  <line x1="50" y1="45" x2="350" y2="45" stroke="#555" strokeWidth="1" />
                  
                  <text x="80" y="75" textAnchor="middle" fontSize="16" fill="#0d47a1" fontWeight="bold">Iodine</text>
                  <text x="200" y="75" textAnchor="middle" fontSize="16" fill="#1b5e20" fontWeight="bold">Selenium</text>
                  <text x="320" y="75" textAnchor="middle" fontSize="16" fill="#e65100" fontWeight="bold">Iron</text>
                </g>
              </svg>
            </div>
          </div>
          <KeyTakeaway>
            Thyroid hormone synthesis is a complex, multi-step process requiring adequate iodine intake (≈150 μg/day), functioning thyroid peroxidase (TPO), and sufficient selenium and iron as cofactors. This biochemical pathway produces primarily T4 (80%) with some T3 (20%), with T4 requiring peripheral conversion to the more active T3 form in target tissues. Deficiencies in essential nutrients or disruption to any step can significantly impair thyroid function even when TSH appears normal.
          </KeyTakeaway>
        </>
      )
    },
    {
      title: "Peripheral Conversion: T4 to T3 Transformation",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-4">
              Most of the active T3 in the body is generated outside the thyroid through the peripheral conversion of T4. This critical process is regulated by selenium-dependent deiodinase enzymes and various physiological factors.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="26" fontWeight="bold" fill="#333">Peripheral Conversion of Thyroid Hormones</text>
                
                {/* Main diagram: T4 → T3 and T4 → rT3 */}
                <g transform="translate(50, 100)">
                  {/* T4 Source */}
                  <rect x="250" y="0" width="160" height="80" rx="10" fill="#e3f2fd" stroke="#2196f3" strokeWidth="2.5" />
                  <text x="330" y="35" textAnchor="middle" fontSize="22" fontWeight="bold" fill="#0d47a1">T4</text>
                  <text x="330" y="60" textAnchor="middle" fontSize="14" fill="#333">Circulating Thyroxine</text>
                  
                  {/* Deiodinase Type 1 (D1) */}
                  <g transform="translate(0, 180)">
                    <circle cx="100" cy="50" r="60" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2.5" />
                    <text x="100" y="35" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#1b5e20">D1</text>
                    <text x="100" y="60" textAnchor="middle" fontSize="14" fill="#333">Type 1 Deiodinase</text>
                    <text x="100" y="80" textAnchor="middle" fontSize="12" fill="#333">Liver, Kidney, Thyroid</text>
                  </g>
                  
                  {/* Deiodinase Type 2 (D2) */}
                  <g transform="translate(220, 180)">
                    <circle cx="100" cy="50" r="60" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2.5" />
                    <text x="100" y="35" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#1b5e20">D2</text>
                    <text x="100" y="60" textAnchor="middle" fontSize="14" fill="#333">Type 2 Deiodinase</text>
                    <text x="100" y="80" textAnchor="middle" fontSize="12" fill="#333">Brain, Muscle, Pituitary</text>
                  </g>
                  
                  {/* Deiodinase Type 3 (D3) */}
                  <g transform="translate(440, 180)">
                    <circle cx="100" cy="50" r="60" fill="#ffebee" stroke="#f44336" strokeWidth="2.5" />
                    <text x="100" y="35" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#b71c1c">D3</text>
                    <text x="100" y="60" textAnchor="middle" fontSize="14" fill="#333">Type 3 Deiodinase</text>
                    <text x="100" y="80" textAnchor="middle" fontSize="12" fill="#333">Activated during stress</text>
                  </g>
                  
                  {/* T3 (Active) */}
                  <g transform="translate(110, 320)">
                    <rect x="0" y="0" width="160" height="80" rx="10" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2.5" />
                    <text x="80" y="35" textAnchor="middle" fontSize="22" fontWeight="bold" fill="#1b5e20">T3</text>
                    <text x="80" y="60" textAnchor="middle" fontSize="14" fill="#333">Active Hormone</text>
                  </g>
                  
                  {/* rT3 (Inactive) */}
                  <g transform="translate(440, 320)">
                    <rect x="0" y="0" width="160" height="80" rx="10" fill="#ffebee" stroke="#f44336" strokeWidth="2.5" />
                    <text x="80" y="35" textAnchor="middle" fontSize="22" fontWeight="bold" fill="#b71c1c">rT3</text>
                    <text x="80" y="60" textAnchor="middle" fontSize="14" fill="#333">Inactive Metabolite</text>
                  </g>
                  
                  {/* Connecting arrows */}
                  <path d="M330,80 L330,120" fill="none" stroke="#333" strokeWidth="2" />
                  
                  {/* Arrows to D1, D2, D3 */}
                  <path d="M330,120 L100,180" fill="none" stroke="#333" strokeWidth="2" markerEnd="url(#arrowhead)" />
                  <path d="M330,120 L320,180" fill="none" stroke="#333" strokeWidth="2" markerEnd="url(#arrowhead)" />
                  <path d="M330,120 L540,180" fill="none" stroke="#333" strokeWidth="2" markerEnd="url(#arrowhead)" />
                  
                  {/* Arrows from deiodinases to products */}
                  <path d="M100,240 L100,300 L110,320" fill="none" stroke="#4caf50" strokeWidth="2" markerEnd="url(#arrowhead)" />
                  <path d="M320,240 L320,300 L270,320" fill="none" stroke="#4caf50" strokeWidth="2" markerEnd="url(#arrowhead)" />
                  <path d="M540,240 L540,300 L520,320" fill="none" stroke="#f44336" strokeWidth="2" markerEnd="url(#arrowhead)" />
                </g>
                
                {/* Factors affecting conversion - moved down to avoid overlap */}
                <g transform="translate(50, 500)">
                  <rect x="0" y="0" width="690" height="100" rx="10" fill="#f0f0f0" stroke="#333" strokeWidth="2" />
                  <text x="345" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#333">Factors Affecting Conversion</text>
                  <line x1="50" y1="40" x2="640" y2="40" stroke="#333" strokeWidth="1" />
                  
                  <g transform="translate(30, 65)">
                    <circle cx="10" cy="0" r="5" fill="#b71c1c" />
                    <text x="25" y="5" fontSize="14" fill="#333">Stress & High Cortisol</text>
                  </g>
                  
                  <g transform="translate(175, 65)">
                    <circle cx="10" cy="0" r="5" fill="#b71c1c" />
                    <text x="25" y="5" fontSize="14" fill="#333">Nutrient Deficiencies</text>
                  </g>
                  
                  <g transform="translate(335, 65)">
                    <circle cx="10" cy="0" r="5" fill="#b71c1c" />
                    <text x="25" y="5" fontSize="14" fill="#333">Inflammation</text>
                  </g>
                  
                  <g transform="translate(440, 65)">
                    <circle cx="10" cy="0" r="5" fill="#b71c1c" />
                    <text x="25" y="5" fontSize="14" fill="#333">Fasting/Caloric Restriction</text>
                  </g>
                  
                  <g transform="translate(620, 65)">
                    <circle cx="10" cy="0" r="5" fill="#b71c1c" />
                    <text x="25" y="5" fontSize="14" fill="#333">Aging</text>
                  </g>
                </g>
              </svg>
            </div>
          </div>
          <KeyTakeaway variant="success">
            Peripheral conversion of T4 to T3 is mediated by selenium-dependent deiodinase enzymes (D1, D2) primarily in the liver, kidneys, brain, and muscle. This process can be significantly disrupted by stress hormones, inflammation, fasting, illness, and nutritional deficiencies—particularly selenium and zinc. Clinically, this means patients can have adequate thyroid gland function and normal TSH/T4 levels but still experience hypothyroid symptoms due to poor peripheral conversion to the active T3 form.
          </KeyTakeaway>
        </>
      )
    },
    {
      title: "Thyroid Hormone Transport and Receptor Binding",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-4">
              Thyroid hormones circulate mostly bound to plasma proteins and require specialized transporters to enter cells. Once inside, T3 binds to nuclear receptors to regulate gene expression, while non-genomic pathways also play important roles.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="26" fontWeight="bold" fill="#333">Thyroid Hormone Transport and Action</text>
                
                {/* Blood stream section */}
                <g transform="translate(30, 110)">
                  <rect x="0" y="0" width="150" height="300" rx="10" fill="#e3f2fd" stroke="#2196f3" strokeWidth="2.5" />
                  <text x="75" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#0d47a1">Bloodstream</text>
                  
                  {/* TBG Binding */}
                  <g transform="translate(20, 80)">
                    <rect x="0" y="0" width="110" height="60" rx="5" fill="white" stroke="#0d47a1" strokeWidth="1.5" />
                    <text x="55" y="25" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#0d47a1">TBG</text>
                    <text x="55" y="45" textAnchor="middle" fontSize="14" fill="#555">~75% of T4/T3</text>
                  </g>
                  
                  {/* Albumin Binding */}
                  <g transform="translate(20, 160)">
                    <rect x="0" y="0" width="110" height="60" rx="5" fill="white" stroke="#0d47a1" strokeWidth="1.5" />
                    <text x="55" y="25" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#0d47a1">Albumin</text>
                    <text x="55" y="45" textAnchor="middle" fontSize="14" fill="#555">~20% of T4/T3</text>
                  </g>
                  
                  {/* Free Hormone */}
                  <g transform="translate(20, 240)">
                    <rect x="0" y="0" width="110" height="40" rx="5" fill="#bbdefb" stroke="#0d47a1" strokeWidth="1.5" />
                    <text x="55" y="25" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333">Free T4/T3 (~0.3%)</text>
                  </g>
                </g>
                
                {/* Cell membrane and transporters */}
                <g transform="translate(230, 110)">
                  <rect x="0" y="0" width="60" height="300" rx="0" fill="#a5d6a7" stroke="#1b5e20" strokeWidth="2.5" />
                  <text x="30" y="30" textAnchor="middle" transform="rotate(90, 30, 30)" fontSize="18" fontWeight="bold" fill="#1b5e20">Cell Membrane</text>
                  
                  {/* Transporters */}
                  <g transform="translate(5, 130)">
                    <rect x="0" y="0" width="50" height="30" rx="15" fill="#1b5e20" />
                    <text x="25" y="20" textAnchor="middle" fontSize="14" fill="white">MCT8</text>
                  </g>
                  
                  <g transform="translate(5, 170)">
                    <rect x="0" y="0" width="50" height="30" rx="15" fill="#1b5e20" />
                    <text x="25" y="20" textAnchor="middle" fontSize="14" fill="white">MCT10</text>
                  </g>
                  
                  <g transform="translate(5, 210)">
                    <rect x="0" y="0" width="50" height="30" rx="15" fill="#1b5e20" />
                    <text x="25" y="20" textAnchor="middle" fontSize="14" fill="white">OATP</text>
                  </g>
                </g>
                
                {/* Cytoplasm */}
                <g transform="translate(340, 110)">
                  <rect x="0" y="0" width="430" height="300" rx="0" fill="#f5f5f5" stroke="#555" strokeWidth="1" />
                  <text x="50" y="30" fontSize="18" fontWeight="bold" fill="#333">Cytoplasm</text>
                  
                  {/* Nucleus */}
                  <g transform="translate(80, 70)">
                    <ellipse cx="140" cy="100" rx="120" ry="90" fill="white" stroke="#555" strokeWidth="2" />
                    <text x="140" y="40" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#333">Nucleus</text>
                    
                    {/* Thyroid Receptor */}
                    <g transform="translate(70, 80)">
                      <rect x="0" y="0" width="140" height="60" rx="5" fill="#e8f5e9" stroke="#1b5e20" strokeWidth="2" />
                      <text x="70" y="25" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#1b5e20">TR Receptors</text>
                      <text x="70" y="45" textAnchor="middle" fontSize="14" fill="#333">TRα and TRβ</text>
                    </g>
                  </g>
                  
                  {/* Non-genomic pathways */}
                  <g transform="translate(300, 220)">
                    <rect x="0" y="0" width="120" height="60" rx="5" fill="#fff3e0" stroke="#e65100" strokeWidth="2" />
                    <text x="60" y="25" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#e65100">Non-genomic</text>
                    <text x="60" y="45" textAnchor="middle" fontSize="14" fill="#333">Rapid effects</text>
                  </g>
                </g>
                
                {/* Arrows indicating movement */}
                <g>
                  {/* Free hormone to transporters */}
                  <path d="M180,290 L230,290" fill="none" stroke="#333" strokeWidth="2" markerEnd="url(#arrowhead)" />
                  
                  {/* Through transporters to cytoplasm */}
                  <path d="M290,290 L340,290" fill="none" stroke="#333" strokeWidth="2" markerEnd="url(#arrowhead)" />
                  
                  {/* To nucleus */}
                  <path d="M400,290 L500,250" fill="none" stroke="#333" strokeWidth="2" markerEnd="url(#arrowhead)" />
                </g>
                
                {/* Effects section */}
                <g transform="translate(50, 430)">
                  <rect x="0" y="0" width="700" height="100" rx="10" fill="white" stroke="#333" strokeWidth="2" />
                  <text x="350" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#333">Tissue-Specific Effects</text>
                  <line x1="50" y1="40" x2="650" y2="40" stroke="#333" strokeWidth="1" />
                  
                  <g transform="translate(70, 70)">
                    <text fontSize="16" fontWeight="bold" fill="#1b5e20">Brain:</text>
                    <text x="70" fontSize="14" fill="#333">Cognitive function</text>
                  </g>
                  
                  <g transform="translate(210, 70)">
                    <text fontSize="16" fontWeight="bold" fill="#0d47a1">Heart:</text>
                    <text x="70" fontSize="14" fill="#333">Rate/contractility</text>
                  </g>
                  
                  <g transform="translate(370, 70)">
                    <text fontSize="16" fontWeight="bold" fill="#e65100">Liver:</text>
                    <text x="70" fontSize="14" fill="#333">Metabolism/Cholesterol</text>
                  </g>
                  
                  <g transform="translate(540, 70)">
                    <text fontSize="16" fontWeight="bold" fill="#6a1b9a">Bone:</text>
                    <text x="60" fontSize="14" fill="#333">Turnover</text>
                  </g>
                </g>
              </svg>
            </div>
          </div>
          <KeyTakeaway variant="warning">
            Thyroid hormones are primarily transported bound to proteins like TBG (Thyroid Binding Globulin) and albumin, with only a small fraction (0.03% of T4, 0.3% of T3) circulating free. This free fraction must then enter cells via specific transporters like MCT8 (crucial for CNS) before binding to nuclear thyroid receptors (TRα/TRβ) to regulate gene expression. Notably, genetic defects in the MCT8 transporter cause severe neurological symptoms despite normal thyroid blood tests, while non-genomic pathways mediate rapid cellular effects. Different tissues express varying levels of TRα/TRβ, explaining tissue-specific responses to thyroid hormone fluctuations.
          </KeyTakeaway>
        </>
      )
    },
    {
      title: "Clinical Assessment: Biomarkers and Functional Testing",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-4">
              Standard thyroid panels often miss subtle dysfunctions due to their limited scope and outdated reference ranges. A comprehensive assessment includes multiple biomarkers and consideration of clinical symptoms.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="26" fontWeight="bold" fill="#333">Comprehensive Thyroid Assessment</text>
                
                {/* Standard Assessment Section */}
                <g transform="translate(50, 100)">
                  <rect x="0" y="0" width="280" height="160" rx="10" fill="#e3f2fd" stroke="#2196f3" strokeWidth="2.5" />
                  <text x="140" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#0d47a1">Standard Biomarkers</text>
                  <line x1="40" y1="45" x2="240" y2="45" stroke="#0d47a1" strokeWidth="1.5" />
                  
                  <g transform="translate(30, 65)">
                    <text fontSize="16" fontWeight="bold" fill="#0d47a1">TSH</text>
                    <text x="150" fontSize="14" fill="#333">0.4 - 4.0 mIU/L (conventional)</text>
                    <text x="150" y="20" fontSize="14" fill="#333">0.5 - 2.0 mIU/L (optimal)</text>
                  </g>
                  
                  <g transform="translate(30, 100)">
                    <text fontSize="16" fontWeight="bold" fill="#0d47a1">Free T4</text>
                    <text x="150" fontSize="14" fill="#333">0.8 - 1.8 ng/dL</text>
                  </g>
                  
                  <g transform="translate(30, 130)">
                    <text fontSize="16" fontWeight="bold" fill="#0d47a1">Free T3</text>
                    <text x="150" fontSize="14" fill="#333">2.3 - 4.2 pg/mL</text>
                  </g>
                </g>
                
                {/* Extended Assessment Section */}
                <g transform="translate(50, 280)">
                  <rect x="0" y="0" width="280" height="190" rx="10" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2.5" />
                  <text x="140" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#1b5e20">Extended Biomarkers</text>
                  <line x1="40" y1="45" x2="240" y2="45" stroke="#1b5e20" strokeWidth="1.5" />
                  
                  <g transform="translate(30, 65)">
                    <text fontSize="16" fontWeight="bold" fill="#1b5e20">Reverse T3</text>
                    <text x="150" fontSize="14" fill="#333">&lt; 20 ng/dL (optimal: 9-15)</text>
                  </g>
                  
                  <g transform="translate(30, 95)">
                    <text fontSize="16" fontWeight="bold" fill="#1b5e20">T3/rT3 Ratio</text>
                    <text x="150" fontSize="14" fill="#333">&gt; 20 (optimal)</text>
                  </g>
                  
                  <g transform="translate(30, 125)">
                    <text fontSize="16" fontWeight="bold" fill="#1b5e20">TPO Antibodies</text>
                    <text x="150" fontSize="14" fill="#333">&lt; 34 IU/mL</text>
                  </g>
                  
                  <g transform="translate(30, 155)">
                    <text fontSize="16" fontWeight="bold" fill="#1b5e20">TG Antibodies</text>
                    <text x="150" fontSize="14" fill="#333">&lt; 115 IU/mL</text>
                  </g>
                </g>
                
                {/* Functional Assessment Section */}
                <g transform="translate(370, 100)">
                  <rect x="0" y="0" width="380" height="370" rx="10" fill="#fff3e0" stroke="#ff9800" strokeWidth="2.5" />
                  <text x="190" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#e65100">Functional Assessment</text>
                  <line x1="40" y1="45" x2="340" y2="45" stroke="#e65100" strokeWidth="1.5" />
                  
                  {/* Metabolism Section */}
                  <g transform="translate(40, 70)">
                    <text fontSize="16" fontWeight="bold" fill="#e65100">Metabolic Indicators</text>
                    <text x="30" y="25" fontSize="14" fill="#333">• Basal body temperature (97.8-98.2°F)</text>
                    <text x="30" y="50" fontSize="14" fill="#333">• Resting heart rate (60-75 BPM)</text>
                    <text x="30" y="75" fontSize="14" fill="#333">• Basal metabolic rate</text>
                  </g>
                  
                  {/* Nutrients Section */}
                  <g transform="translate(40, 160)">
                    <text fontSize="16" fontWeight="bold" fill="#e65100">Nutrient Status</text>
                    <text x="30" y="25" fontSize="14" fill="#333">• Ferritin (40-90 ng/mL)</text>
                    <text x="30" y="50" fontSize="14" fill="#333">• Selenium (120-160 μg/L)</text>
                    <text x="30" y="75" fontSize="14" fill="#333">• Zinc (90-130 μg/dL)</text>
                    <text x="30" y="100" fontSize="14" fill="#333">• Iodine (100-300 μg/L)</text>
                  </g>
                  
                  {/* Hormones Section */}
                  <g transform="translate(40, 280)">
                    <text fontSize="16" fontWeight="bold" fill="#e65100">Hormone Interactions</text>
                    <text x="30" y="25" fontSize="14" fill="#333">• Cortisol (morning 10-20 μg/dL)</text>
                    <text x="30" y="50" fontSize="14" fill="#333">• Insulin resistance markers</text>
                    <text x="30" y="75" fontSize="14" fill="#333">• Sex hormone levels</text>
                  </g>
                </g>
                
                {/* Bottom Reference Box */}
                <g transform="translate(50, 490)">
                  <rect x="0" y="0" width="700" height="40" rx="5" fill="#f0f0f0" stroke="#555" strokeWidth="1" />
                  <text x="350" y="25" textAnchor="middle" fontSize="14" fontStyle="italic" fill="#555">
                    * Optimal ranges may differ based on individual factors and clinical context
                  </text>
                </g>
              </svg>
            </div>
          </div>
          <KeyTakeaway>
            Comprehensive thyroid assessment requires going beyond basic TSH, FT4, and FT3 testing to include antibodies (TPO, TG), reverse T3, and the T3/rT3 ratio—which can reveal conversion problems. Clinical assessment should also evaluate basal body temperature, heart rate, and critical nutrient status (ferritin, selenium, zinc, iodine). Remember that TSH reference ranges (0.4-4.0 mIU/L) are based on statistical averages that include many subclinically hypothyroid individuals; optimal function typically occurs with TSH between 0.5-2.0 mIU/L, morning temperatures between 97.8-98.2°F, and strong metabolic indicators.
          </KeyTakeaway>
        </>
      )
    },
    {
      title: "Thyroid-Adrenal-Insulin Axis Connections",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-4">
              Thyroid function is intricately connected with other hormone systems, particularly the HPA axis (cortisol) and insulin signaling. These connections explain why thyroid disorders often manifest with symptoms related to stress, metabolism, and energy regulation.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="26" fontWeight="bold" fill="#333">Hormonal Crosstalk with Thyroid Function</text>
                
                {/* Main diagram - three connected systems */}
                <g transform="translate(50, 100)">
                  {/* Thyroid System */}
                  <g transform="translate(250, 0)">
                    <circle cx="100" cy="80" r="80" fill="#e3f2fd" stroke="#2196f3" strokeWidth="2.5" />
                    <text x="100" y="50" textAnchor="middle" fontSize="22" fontWeight="bold" fill="#0d47a1">Thyroid</text>
                    <text x="100" y="80" textAnchor="middle" fontSize="16" fill="#333">T4 / T3</text>
                    <text x="100" y="105" textAnchor="middle" fontSize="14" fill="#333">Metabolic Regulation</text>
                  </g>
                  
                  {/* Adrenal System */}
                  <g transform="translate(50, 250)">
                    <circle cx="100" cy="80" r="80" fill="#fff3e0" stroke="#ff9800" strokeWidth="2.5" />
                    <text x="100" y="50" textAnchor="middle" fontSize="22" fontWeight="bold" fill="#e65100">Adrenal</text>
                    <text x="100" y="80" textAnchor="middle" fontSize="16" fill="#333">Cortisol</text>
                    <text x="100" y="105" textAnchor="middle" fontSize="14" fill="#333">Stress Response</text>
                  </g>
                  
                  {/* Insulin System */}
                  <g transform="translate(450, 250)">
                    <circle cx="100" cy="80" r="80" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2.5" />
                    <text x="100" y="50" textAnchor="middle" fontSize="22" fontWeight="bold" fill="#1b5e20">Pancreas</text>
                    <text x="100" y="80" textAnchor="middle" fontSize="16" fill="#333">Insulin / Leptin</text>
                    <text x="100" y="105" textAnchor="middle" fontSize="14" fill="#333">Energy Storage</text>
                  </g>
                  
                  {/* Connecting Lines with Bidirectional Arrows */}
                  <g>
                    {/* Thyroid to Adrenal */}
                    <path d="M250,120 L180,260" fill="none" stroke="#333" strokeWidth="2" />
                    
                    {/* Thyroid to Insulin */}
                    <path d="M400,120 L470,260" fill="none" stroke="#333" strokeWidth="2" />
                    
                    {/* Adrenal to Insulin */}
                    <path d="M230,330 L420,330" fill="none" stroke="#333" strokeWidth="2" />
                    
                    {/* Arrowheads */}
                    <circle cx="180" cy="260" r="6" fill="#333" />
                    <circle cx="250" cy="120" r="6" fill="#333" />
                    <circle cx="400" cy="120" r="6" fill="#333" />
                    <circle cx="470" cy="260" r="6" fill="#333" />
                    <circle cx="230" cy="330" r="6" fill="#333" />
                    <circle cx="420" cy="330" r="6" fill="#333" />
                  </g>
                </g>
                
                {/* Interaction descriptions */}
                <g transform="translate(50, 430)">
                  <rect x="0" y="0" width="700" height="100" rx="10" fill="white" stroke="#333" strokeWidth="2" />
                  <line x1="50" y1="25" x2="650" y2="25" stroke="#333" strokeWidth="1" />
                  
                  <g transform="translate(20, 45)">
                    <text x="0" y="0" fontSize="16" fontWeight="bold" fill="#0d47a1">Thyroid-Adrenal:</text>
                    <text x="140" y="0" fontSize="14" fill="#333">High cortisol reduces T4→T3 conversion</text>
                    <text x="140" y="20" fontSize="14" fill="#333">Low cortisol blunts thyroid hormone effects</text>
                  </g>
                  
                  <g transform="translate(380, 45)">
                    <text x="0" y="0" fontSize="16" fontWeight="bold" fill="#1b5e20">Thyroid-Insulin:</text>
                    <text x="140" y="0" fontSize="14" fill="#333">Hypothyroidism promotes insulin resistance</text>
                    <text x="140" y="20" fontSize="14" fill="#333">Fasting/low leptin reduces T3 production</text>
                  </g>
                </g>
              </svg>
            </div>
          </div>
          <KeyTakeaway variant="warning">
            The thyroid system is deeply interconnected with adrenal function and insulin signaling. High cortisol from chronic stress suppresses TSH and inhibits T4 to T3 conversion, while low cortisol can blunt thyroid response even when labs are "normal." Similarly, caloric restriction or fasting reduces T3 production (a survival mechanism), while hypothyroidism promotes insulin resistance and metabolic syndrome. Clinically, this means thyroid treatment may be ineffective without addressing concurrent adrenal dysfunction or metabolic issues, and thyroid optimization often requires a whole-system approach including stress management and balanced nutrition.
          </KeyTakeaway>
        </>
      )
    },
    {
      title: "Mitochondrial Effects of Thyroid Hormones",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-4">
              Thyroid hormones profoundly influence mitochondrial function and oxidative metabolism. T3 increases mitochondrial biogenesis, enhances respiratory chain activity, and regulates cellular energy production in virtually all tissues.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="26" fontWeight="bold" fill="#333">Thyroid Hormone and Mitochondrial Function</text>
                
                {/* Cell and Mitochondria Diagram */}
                <g transform="translate(50, 100)">
                  {/* Cell Membrane */}
                  <rect x="0" y="0" width="700" height="330" rx="20" fill="#f5f5f5" stroke="#555" strokeWidth="2" />
                  <text x="350" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#333">Cell Cytoplasm</text>
                  
                  {/* Mitochondrion 1 */}
                  <g transform="translate(150, 100)">
                    <ellipse cx="100" cy="70" rx="100" ry="60" fill="#ffcdd2" stroke="#d32f2f" strokeWidth="2.5" />
                    <ellipse cx="100" cy="70" rx="80" ry="45" fill="#ffebee" stroke="#d32f2f" strokeWidth="1" />
                    <text x="100" y="75" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#b71c1c">Mitochondrion</text>
                  </g>
                  
                  {/* Mitochondrion 2 */}
                  <g transform="translate(400, 100)">
                    <ellipse cx="100" cy="70" rx="100" ry="60" fill="#ffcdd2" stroke="#d32f2f" strokeWidth="2.5" />
                    <ellipse cx="100" cy="70" rx="80" ry="45" fill="#ffebee" stroke="#d32f2f" strokeWidth="1" />
                  </g>
                  
                  {/* Nucleus */}
                  <g transform="translate(270, 210)">
                    <circle cx="80" cy="70" r="65" fill="white" stroke="#333" strokeWidth="2" />
                    <text x="80" y="75" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">Nucleus</text>
                  </g>
                  
                  {/* T3 effects on mitochondria */}
                  <g transform="translate(150, 50)">
                    <path d="M0,0 L50,30" fill="none" stroke="#e65100" strokeWidth="2" markerEnd="url(#arrowhead)" />
                    <text x="-15" y="-5" fontSize="16" fontWeight="bold" fill="#e65100">T3</text>
                  </g>
                  
                  <g transform="translate(400, 50)">
                    <path d="M0,0 L50,30" fill="none" stroke="#e65100" strokeWidth="2" markerEnd="url(#arrowhead)" />
                    <text x="-15" y="-5" fontSize="16" fontWeight="bold" fill="#e65100">T3</text>
                  </g>
                  
                  {/* T3 effects on nucleus */}
                  <g transform="translate(360, 190)">
                    <path d="M0,0 L-30,40" fill="none" stroke="#e65100" strokeWidth="2" markerEnd="url(#arrowhead)" />
                    <text x="10" y="5" fontSize="16" fontWeight="bold" fill="#e65100">T3</text>
                  </g>
                </g>
                
                {/* Effect descriptions */}
                <g transform="translate(50, 450)">
                  <rect x="0" y="0" width="700" height="80" rx="10" fill="white" stroke="#333" strokeWidth="2" />
                  
                  <g transform="translate(20, 25)">
                    <text x="0" y="0" fontSize="16" fontWeight="bold" fill="#b71c1c">Direct Effects:</text>
                    <text x="120" y="0" fontSize="14" fill="#333">↑ Mitochondrial biogenesis</text>
                    <text x="120" y="25" fontSize="14" fill="#333">↑ ATP synthesis & oxygen consumption</text>
                  </g>
                  
                  <g transform="translate(370, 25)">
                    <text x="0" y="0" fontSize="16" fontWeight="bold" fill="#1b5e20">Genomic Effects:</text>
                    <text x="120" y="0" fontSize="14" fill="#333">↑ Uncoupling proteins (heat)</text>
                    <text x="120" y="25" fontSize="14" fill="#333">↑ β-oxidation & metabolic rate</text>
                  </g>
                </g>
              </svg>
            </div>
          </div>
          <KeyTakeaway>
            Thyroid hormones are primary regulators of cellular energy metabolism through their effects on mitochondria. T3 increases mitochondrial biogenesis and upregulates enzymes of the respiratory chain, which boosts ATP production and heat generation. In brown adipose tissue, T3 activates UCP1 (uncoupling protein) for thermogenesis. However, increased metabolic activity also generates reactive oxygen species (ROS), requiring balanced antioxidant systems. Interestingly, both hypo- and hyperthyroidism can increase oxidative stress—hypothyroidism through inefficient metabolism and reduced antioxidant enzyme activity, and hyperthyroidism through excessive metabolic rate overwhelming antioxidant defenses.
          </KeyTakeaway>
        </>
      )
    },
    {
      title: "Nutrient Therapy for Thyroid Support",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-4">
              Specific micronutrients serve as essential cofactors for thyroid hormone synthesis, conversion, and function. Optimizing these nutrients can significantly improve thyroid function even without medication in some cases.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="26" fontWeight="bold" fill="#333">Key Nutrients for Thyroid Function</text>
                
                {/* Selenium Section */}
                <g transform="translate(50, 100)">
                  <rect x="0" y="0" width="220" height="150" rx="10" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2.5" />
                  <text x="110" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#1b5e20">Selenium</text>
                  <line x1="30" y1="45" x2="190" y2="45" stroke="#1b5e20" strokeWidth="1.5" />
                  
                  <text x="20" y="70" fontSize="14" fill="#333">• Component of deiodinases</text>
                  <text x="20" y="95" fontSize="14" fill="#333">• Supports T4→T3 conversion</text>
                  <text x="20" y="120" fontSize="14" fill="#333">• Protects thyroid from oxidative</text>
                  <text x="20" y="140" fontSize="14" fill="#333">  damage via glutathione</text>
                </g>
                
                {/* Iodine Section */}
                <g transform="translate(290, 100)">
                  <rect x="0" y="0" width="220" height="150" rx="10" fill="#e3f2fd" stroke="#2196f3" strokeWidth="2.5" />
                  <text x="110" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#0d47a1">Iodine</text>
                  <line x1="30" y1="45" x2="190" y2="45" stroke="#0d47a1" strokeWidth="1.5" />
                  
                  <text x="20" y="70" fontSize="14" fill="#333">• Essential for T4/T3 structure</text>
                  <text x="20" y="95" fontSize="14" fill="#333">• Daily need: ~150 μg</text>
                  <text x="20" y="120" fontSize="14" fill="#333">• Balance: avoid deficiency</text>
                  <text x="20" y="140" fontSize="14" fill="#333">  but excess can trigger autoimmunity</text>
                </g>
                
                {/* Iron Section */}
                <g transform="translate(530, 100)">
                  <rect x="0" y="0" width="220" height="150" rx="10" fill="#fff3e0" stroke="#ff9800" strokeWidth="2.5" />
                  <text x="110" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#e65100">Iron</text>
                  <line x1="30" y1="45" x2="190" y2="45" stroke="#e65100" strokeWidth="1.5" />
                  
                  <text x="20" y="70" fontSize="14" fill="#333">• Cofactor for TPO enzyme</text>
                  <text x="20" y="95" fontSize="14" fill="#333">• Helps make thyroid hormones</text>
                  <text x="20" y="120" fontSize="14" fill="#333">• Target ferritin: 70-100 ng/mL</text>
                  <text x="20" y="140" fontSize="14" fill="#333">• Common deficiency in women</text>
                </g>
                
                {/* Zinc Section */}
                <g transform="translate(50, 270)">
                  <rect x="0" y="0" width="220" height="150" rx="10" fill="#f3e5f5" stroke="#9c27b0" strokeWidth="2.5" />
                  <text x="110" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#6a1b9a">Zinc</text>
                  <line x1="30" y1="45" x2="190" y2="45" stroke="#6a1b9a" strokeWidth="1.5" />
                  
                  <text x="20" y="70" fontSize="14" fill="#333">• Needed for T3 receptors</text>
                  <text x="20" y="95" fontSize="14" fill="#333">• Supports deiodinase enzymes</text>
                  <text x="20" y="120" fontSize="14" fill="#333">• Dose: 20-30 mg daily</text>
                  <text x="20" y="140" fontSize="14" fill="#333">• Include copper (2 mg) if long-term</text>
                </g>
                
                {/* Vitamin D Section */}
                <g transform="translate(290, 270)">
                  <rect x="0" y="0" width="220" height="150" rx="10" fill="#fff9c4" stroke="#ffc107" strokeWidth="2.5" />
                  <text x="110" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#ff6f00">Vitamin D</text>
                  <line x1="30" y1="45" x2="190" y2="45" stroke="#ff6f00" strokeWidth="1.5" />
                  
                  <text x="20" y="70" fontSize="14" fill="#333">• Modulates immune function</text>
                  <text x="20" y="95" fontSize="14" fill="#333">• Reduces TPO antibodies</text>
                  <text x="20" y="120" fontSize="14" fill="#333">• Target: 50-80 ng/mL for</text>
                  <text x="20" y="140" fontSize="14" fill="#333">  autoimmune thyroiditis</text>
                </g>
                
                {/* Myo-Inositol Section */}
                <g transform="translate(530, 270)">
                  <rect x="0" y="0" width="220" height="150" rx="10" fill="#e1f5fe" stroke="#03a9f4" strokeWidth="2.5" />
                  <text x="110" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#0277bd">Myo-Inositol</text>
                  <line x1="30" y1="45" x2="190" y2="45" stroke="#0277bd" strokeWidth="1.5" />
                  
                  <text x="20" y="70" fontSize="14" fill="#333">• Supports TSH signaling</text>
                  <text x="20" y="95" fontSize="14" fill="#333">• Combined with selenium</text>
                  <text x="20" y="120" fontSize="14" fill="#333">• Dose: 600 mg twice daily</text>
                  <text x="20" y="140" fontSize="14" fill="#333">• May normalize subclinical hypo</text>
                </g>
                
                {/* Bottom guidance section */}
                <g transform="translate(50, 440)">
                  <rect x="0" y="0" width="700" height="90" rx="10" fill="white" stroke="#333" strokeWidth="2" />
                  <text x="350" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#333">Clinical Application</text>
                  <line x1="50" y1="45" x2="650" y2="45" stroke="#333" strokeWidth="1" />
                  
                  <text x="50" y="70" fontSize="14" fill="#333">
                    Start with selenium (200 mcg) + myo-inositol (600 mg) for subclinical hypothyroidism or
                  </text>
                  <text x="50" y="90" fontSize="14" fill="#333">
                    autoimmune thyroiditis. Add iron, zinc, and vitamin D based on lab results.
                  </text>
                </g>
              </svg>
            </div>
          </div>
          <KeyTakeaway variant="success">
            Selenium supplementation (200 mcg/day) is foundational for thyroid support, reducing TPO antibodies in Hashimoto's and enhancing T4-to-T3 conversion through improved deiodinase function. Combining selenium with myo-inositol (600 mg/day) has shown remarkable results in clinical trials—significantly decreasing TSH and antibody levels while improving free T4/T3 in subclinical hypothyroidism. Other key nutrients include zinc (for deiodinase enzymes and receptor binding), iron (for thyroid peroxidase activity), and vitamin D (for immune modulation). This nutrient approach is particularly effective for borderline or subclinical cases, potentially delaying or preventing the need for medication.
          </KeyTakeaway>
        </>
      )
    },
    {
      title: "Botanical Support for Thyroid Function",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-4">
              Several botanicals have demonstrated thyroid-supportive properties in both traditional medicine systems and modern research. These herbs can modulate thyroid function through various mechanisms.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="26" fontWeight="bold" fill="#333">Herbal Interventions for Thyroid Support</text>
                
                {/* Ashwagandha Section */}
                <g transform="translate(50, 100)">
                  <rect x="0" y="0" width="340" height="180" rx="10" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2.5" />
                  <text x="170" y="30" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#1b5e20">Ashwagandha</text>
                  <text x="170" y="55" textAnchor="middle" fontSize="16" fontStyle="italic" fill="#555">(Withania somnifera)</text>
                  <line x1="50" y1="70" x2="290" y2="70" stroke="#1b5e20" strokeWidth="1.5" />
                  
                  <text x="20" y="95" fontSize="15" fill="#333">• Adaptogenic herb that reduces stress</text>
                  <text x="20" y="120" fontSize="15" fill="#333">• Clinical trial showed improved TSH, T4, T3</text>
                  <text x="20" y="145" fontSize="15" fill="#333">• Dosage: 500-600 mg root extract daily</text>
                  <text x="20" y="170" fontSize="15" fill="#333">• Effective for subclinical hypothyroidism</text>
                </g>
                
                {/* Guggul Section */}
                <g transform="translate(410, 100)">
                  <rect x="0" y="0" width="340" height="180" rx="10" fill="#fff3e0" stroke="#ff9800" strokeWidth="2.5" />
                  <text x="170" y="30" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#e65100">Guggul</text>
                  <text x="170" y="55" textAnchor="middle" fontSize="16" fontStyle="italic" fill="#555">(Commiphora mukul)</text>
                  <line x1="50" y1="70" x2="290" y2="70" stroke="#e65100" strokeWidth="1.5" />
                  
                  <text x="20" y="95" fontSize="15" fill="#333">• Ayurvedic resin that stimulates conversion</text>
                  <text x="20" y="120" fontSize="15" fill="#333">• Increases T3 levels and T3/T4 ratio</text>
                  <text x="20" y="145" fontSize="15" fill="#333">• Dosage: 250-500 mg standardized extract</text>
                  <text x="20" y="170" fontSize="15" fill="#333">• Also improves cholesterol metabolism</text>
                </g>
                
                {/* Coleus Section */}
                <g transform="translate(50, 300)">
                  <rect x="0" y="0" width="340" height="180" rx="10" fill="#e3f2fd" stroke="#2196f3" strokeWidth="2.5" />
                  <text x="170" y="30" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#0d47a1">Coleus Forskohlii</text>
                  <text x="170" y="55" textAnchor="middle" fontSize="16" fontStyle="italic" fill="#555">(Forskolin)</text>
                  <line x1="50" y1="70" x2="290" y2="70" stroke="#0d47a1" strokeWidth="1.5" />
                  
                  <text x="20" y="95" fontSize="15" fill="#333">• Increases cAMP, activates cellular signals</text>
                  <text x="20" y="120" fontSize="15" fill="#333">• May enhance TSH effects on thyroid cells</text>
                  <text x="20" y="145" fontSize="15" fill="#333">• Dosage: 50-100 mg forskolin (10% extract)</text>
                  <text x="20" y="170" fontSize="15" fill="#333">• Also supports metabolism and lipolysis</text>
                </g>
                
                {/* Bacopa Section */}
                <g transform="translate(410, 300)">
                  <rect x="0" y="0" width="340" height="180" rx="10" fill="#f3e5f5" stroke="#9c27b0" strokeWidth="2.5" />
                  <text x="170" y="30" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#6a1b9a">Bacopa Monnieri</text>
                  <text x="170" y="55" textAnchor="middle" fontSize="16" fontStyle="italic" fill="#555">(Brahmi)</text>
                  <line x1="50" y1="70" x2="290" y2="70" stroke="#6a1b9a" strokeWidth="1.5" />
                  
                  <text x="20" y="95" fontSize="15" fill="#333">• Enhances T4 synthesis in animal studies</text>
                  <text x="20" y="120" fontSize="15" fill="#333">• Supports cognitive function with thyroid</text>
                  <text x="20" y="145" fontSize="15" fill="#333">• Dosage: 300-450 mg standardized extract</text>
                  <text x="20" y="170" fontSize="15" fill="#333">• Consider for hypothyroid brain fog</text>
                </g>
              </svg>
            </div>
          </div>
          <KeyTakeaway>
            Ashwagandha has demonstrated significant thyroid-enhancing effects in clinical research, with one randomized controlled trial showing normalization of TSH, T4, and T3 levels in subclinical hypothyroid patients after just 8 weeks (600 mg daily). It works by reducing cortisol, protecting thyroid cells from oxidative damage, and potentially stimulating the HPT axis. Guggul (Commiphora mukul) enhances T4 to T3 conversion and improves lipid profiles, while herbs like Bacopa monnieri may support T4 synthesis and address cognitive symptoms. These botanicals are particularly useful for mild or subclinical cases and can be combined with nutrient therapies, though they should be used cautiously with prescription thyroid medications.
          </KeyTakeaway>
        </>
      )
    },
    {
      title: "Thyroid Hormone Therapy: Precision Approach",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-4">
              When thyroid hormone replacement is necessary, a personalized approach considering the molecular basis of hormone action is essential. Different therapy options address specific aspects of thyroid physiology.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="26" fontWeight="bold" fill="#333">Thyroid Hormone Replacement Options</text>
                
                {/* Main comparison table */}
                <g transform="translate(50, 90)">
                  <rect x="0" y="0" width="700" height="350" rx="5" fill="white" stroke="#333" strokeWidth="2" />
                  
                  {/* Table Headers */}
                  <rect x="0" y="0" width="180" height="50" fill="#e3f2fd" stroke="#333" strokeWidth="1" />
                  <rect x="180" y="0" width="260" height="50" fill="#e3f2fd" stroke="#333" strokeWidth="1" />
                  <rect x="440" y="0" width="260" height="50" fill="#e3f2fd" stroke="#333" strokeWidth="1" />
                  
                  <text x="90" y="30" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#0d47a1">Therapy Type</text>
                  <text x="310" y="30" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#0d47a1">Advantages</text>
                  <text x="570" y="30" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#0d47a1">Best For</text>
                  
                  {/* Levothyroxine (T4) Row */}
                  <rect x="0" y="50" width="180" height="100" fill="#ffffff" stroke="#333" strokeWidth="1" />
                  <rect x="180" y="50" width="260" height="100" fill="#ffffff" stroke="#333" strokeWidth="1" />
                  <rect x="440" y="50" width="260" height="100" fill="#ffffff" stroke="#333" strokeWidth="1" />
                  
                  <text x="90" y="80" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">Levothyroxine (T4)</text>
                  <text x="90" y="100" textAnchor="middle" fontSize="14" fontStyle="italic" fill="#555">Synthroid, Tirosint</text>
                  <text x="90" y="120" textAnchor="middle" fontSize="14" fill="#333">T4-only</text>
                  
                  <g transform="translate(190, 65)">
                    <text x="0" y="0" fontSize="14" fill="#333">• Stable blood levels (long half-life)</text>
                    <text x="0" y="20" fontSize="14" fill="#333">• Well-studied safety profile</text>
                    <text x="0" y="40" fontSize="14" fill="#333">• Body controls T4→T3 conversion</text>
                    <text x="0" y="60" fontSize="14" fill="#333">• Once daily dosing</text>
                  </g>
                  
                  <g transform="translate(450, 65)">
                    <text x="0" y="0" fontSize="14" fill="#333">• Most hypothyroid patients</text>
                    <text x="0" y="20" fontSize="14" fill="#333">• Good converters (normal D1/D2)</text>
                    <text x="0" y="40" fontSize="14" fill="#333">• Pregnancy (stable levels)</text>
                    <text x="0" y="60" fontSize="14" fill="#333">• Initial therapy for most cases</text>
                  </g>
                  
                  {/* T4/T3 Combination Row */}
                  <rect x="0" y="150" width="180" height="100" fill="#f9f9f9" stroke="#333" strokeWidth="1" />
                  <rect x="180" y="150" width="260" height="100" fill="#f9f9f9" stroke="#333" strokeWidth="1" />
                  <rect x="440" y="150" width="260" height="100" fill="#f9f9f9" stroke="#333" strokeWidth="1" />
                  
                  <text x="90" y="180" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">T4/T3 Combination</text>
                  <text x="90" y="200" textAnchor="middle" fontSize="14" fontStyle="italic" fill="#555">Synthetic Combo</text>
                  <text x="90" y="220" textAnchor="middle" fontSize="14" fill="#333">4:1 or 10:1 ratio</text>
                  
                  <g transform="translate(190, 165)">
                    <text x="0" y="0" fontSize="14" fill="#333">• Supplies T3 directly</text>
                    <text x="0" y="20" fontSize="14" fill="#333">• Bypasses conversion issues</text>
                    <text x="0" y="40" fontSize="14" fill="#333">• May improve symptoms in subset</text>
                    <text x="0" y="60" fontSize="14" fill="#333">• Better cognitive outcomes in some</text>
                  </g>
                  
                  <g transform="translate(450, 165)">
                    <text x="0" y="0" fontSize="14" fill="#333">• Poor T4 converters</text>
                    <text x="0" y="20" fontSize="14" fill="#333">• High reverse T3</text>
                    <text x="0" y="40" fontSize="14" fill="#333">• Persistent symptoms on T4 only</text>
                    <text x="0" y="60" fontSize="14" fill="#333">• DIO2 gene polymorphisms</text>
                  </g>
                  
                  {/* Desiccated Thyroid Row */}
                  <rect x="0" y="250" width="180" height="100" fill="#ffffff" stroke="#333" strokeWidth="1" />
                  <rect x="180" y="250" width="260" height="100" fill="#ffffff" stroke="#333" strokeWidth="1" />
                  <rect x="440" y="250" width="260" height="100" fill="#ffffff" stroke="#333" strokeWidth="1" />
                  
                  <text x="90" y="280" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">Desiccated Thyroid</text>
                  <text x="90" y="300" textAnchor="middle" fontSize="14" fontStyle="italic" fill="#555">NDT, Armour, Nature-Throid</text>
                  <text x="90" y="320" textAnchor="middle" fontSize="14" fill="#333">~4:1 T4:T3 ratio + T2, T1</text>
                  
                  <g transform="translate(190, 265)">
                    <text x="0" y="0" fontSize="14" fill="#333">• Contains all thyroid hormones</text>
                    <text x="0" y="20" fontSize="14" fill="#333">• More natural T4:T3 ratio</text>
                    <text x="0" y="40" fontSize="14" fill="#333">• Subjective preference by many</text>
                    <text x="0" y="60" fontSize="14" fill="#333">• Long history of clinical use</text>
                  </g>
                  
                  <g transform="translate(450, 265)">
                    <text x="0" y="0" fontSize="14" fill="#333">• Poor responders to synthetics</text>
                    <text x="0" y="20" fontSize="14" fill="#333">• Those seeking "natural" options</text>
                    <text x="0" y="40" fontSize="14" fill="#333">• Patients with both low T4 and T3</text>
                    <text x="0" y="60" fontSize="14" fill="#333">• Monitor for T3 peaks (BID dosing)</text>
                  </g>
                </g>
                
                {/* Bottom note */}
                <g transform="translate(50, 460)">
                  <rect x="0" y="0" width="700" height="70" rx="5" fill="#f0f0f0" stroke="#555" strokeWidth="1" />
                  <text x="350" y="25" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">Dosing Considerations</text>
                  <text x="350" y="50" textAnchor="middle" fontSize="14" fill="#333">
                    Start low (25-50 μg T4 or ½ grain NDT), titrate every 6-8 weeks based on labs and symptoms.
                  </text>
                  <text x="350" y="70" textAnchor="middle" fontSize="14" fill="#333">
                    Target TSH 1-2 mIU/L, FT3 upper half of range for optimal cellular function.
                  </text>
                </g>
              </svg>
            </div>
          </div>
          <KeyTakeaway variant="warning">
            Levothyroxine (T4) is the standard first-line therapy, providing a stable prohormone that the body converts to active T3 as needed. However, some patients with conversion issues (e.g., high reverse T3 or DIO2 gene polymorphisms) may benefit from T4/T3 combination therapy, which directly provides some active hormone. For those seeking a more complete approach, Natural Desiccated Thyroid (NDT) provides all thyroid hormones in a ratio similar to human thyroid. The best approach is individualized based on conversion capacity, symptoms, and patient preference. Always consider that thyroid hormone absorption can be impaired by many factors including food, coffee, calcium, iron supplements, and certain medications—all of which should be separated from thyroid medication by 4+ hours.
          </KeyTakeaway>
        </>
      )
    },
    {
      title: "Clinical Protocol for Thyroid Optimization",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-4">
              This evidence-based protocol integrates foundational approaches with condition-specific strategies for optimizing thyroid function through a functional and longevity medicine lens.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="26" fontWeight="bold" fill="#333">Clinical Protocol Implementation</text>
                
                {/* Phase diagram */}
                <g transform="translate(50, 90)">
                  {/* Assessment box */}
                  <rect x="0" y="0" width="700" height="100" rx="10" fill="#e3f2fd" stroke="#2196f3" strokeWidth="2.5" />
                  <text x="350" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#0d47a1">Phase 1: Assessment</text>
                  <line x1="100" y1="45" x2="600" y2="45" stroke="#0d47a1" strokeWidth="1.5" />
                  
                  <g transform="translate(20, 65)">
                    <text x="0" y="0" fontSize="15" fill="#333">• Comprehensive lab panel: TSH, Free T4, Free T3, Reverse T3, TPO/Tg antibodies</text>
                    <text x="0" y="25" fontSize="15" fill="#333">• Supportive tests: Ferritin, selenium, zinc, vitamin D, morning cortisol, lipids</text>
                  </g>
                  
                  {/* Arrow down */}
                  <path d="M350,100 L350,120" fill="none" stroke="#333" strokeWidth="2" markerEnd="url(#arrowhead)" />
                  
                  {/* Foundations box */}
                  <g transform="translate(0, 120)">
                    <rect x="0" y="0" width="700" height="100" rx="10" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2.5" />
                    <text x="350" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#1b5e20">Phase 2: Foundational Therapy</text>
                    <line x1="100" y1="45" x2="600" y2="45" stroke="#1b5e20" strokeWidth="1.5" />
                    
                    <g transform="translate(20, 65)">
                      <text x="0" y="0" fontSize="15" fill="#333">• Nutrition: selenium (200 mcg), iodine (~150 μg), iron, zinc, vitamin D optimization</text>
                      <text x="0" y="25" fontSize="15" fill="#333">• Lifestyle: stress reduction, sleep optimization, moderate exercise, toxin avoidance</text>
                    </g>
                  </g>
                  
                  {/* Arrow down */}
                  <path d="M350,220 L350,240" fill="none" stroke="#333" strokeWidth="2" markerEnd="url(#arrowhead)" />
                  
                  {/* Condition-specific box */}
                  <g transform="translate(0, 240)">
                    <rect x="0" y="0" width="700" height="120" rx="10" fill="#fff3e0" stroke="#ff9800" strokeWidth="2.5" />
                    <text x="350" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#e65100">Phase 3: Condition-Specific Approaches</text>
                    <line x1="100" y1="45" x2="600" y2="45" stroke="#e65100" strokeWidth="1.5" />
                    
                    <g transform="translate(20, 65)">
                      <text x="0" y="0" fontSize="15" fill="#333">• Subclinical hypothyroidism: myo-inositol + selenium or low-dose levo (25-50 μg)</text>
                      <text x="0" y="25" fontSize="15" fill="#333">• Hashimoto's: LDN (1.5-4.5 mg), gluten-free diet, selenium, vitamin D to 60-80 ng/mL</text>
                      <text x="0" y="50" fontSize="15" fill="#333">• Conversion issues: combo T4/T3, NDT, or add guggul, ashwagandha, address stress</text>
                    </g>
                  </g>
                  
                  {/* Arrow down */}
                  <path d="M350,360 L350,380" fill="none" stroke="#333" strokeWidth="2" markerEnd="url(#arrowhead)" />
                  
                  {/* Monitoring box */}
                  <g transform="translate(0, 380)">
                    <rect x="0" y="0" width="700" height="80" rx="10" fill="#f3e5f5" stroke="#9c27b0" strokeWidth="2.5" />
                    <text x="350" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#6a1b9a">Phase 4: Monitoring & Adjustment</text>
                    <line x1="100" y1="45" x2="600" y2="45" stroke="#6a1b9a" strokeWidth="1.5" />
                    
                    <g transform="translate(20, 65)">
                      <text x="0" y="0" fontSize="15" fill="#333">• Recheck labs every 6-8 weeks initially, then 3-6 months once stable</text>
                      <text x="0" y="25" fontSize="15" fill="#333">• Target: TSH 1-2 μIU/mL, Free T3 upper half of range, antibodies decreasing</text>
                    </g>
                  </g>
                </g>
              </svg>
            </div>
          </div>
          <KeyTakeaway>
            This comprehensive clinical protocol starts with thorough assessment of thyroid function markers, nutrient status, and related hormones. All patients receive foundational therapy including nutrient optimization (selenium, iodine, zinc, iron, vitamin D) and lifestyle interventions (stress reduction, sleep optimization) before considering medication. For subclinical hypothyroidism, evidence suggests myo-inositol plus selenium can normalize TSH in many cases, while autoimmune thyroiditis may benefit from immune modulation with low-dose naltrexone (LDN), gluten elimination, and vitamin D optimization. Medication approach should be personalized—T4 monotherapy works well for most, but those with conversion issues (high reverse T3) may need combination T4/T3 therapy or natural desiccated thyroid. The protocol emphasizes addressing both thyroid function and underlying root causes.
          </KeyTakeaway>
        </>
      )
    }
  ];
  

  return (
    <MentorshipAccessGate>
      <Navbar />
      <div className="container max-w-6xl mx-auto px-4 py-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
            Thyroid: Molecular and Clinical Applications
          </h1>
          <p className="text-[#7F8C8D] mb-6">
            Evidence-based protocol for optimizing thyroid function through a functional and longevity medicine lens.
          </p>
          <div className="flex items-center gap-3 mb-8">
            <div className="flex items-center text-sm text-[#7F8C8D]">
              <FileText className="h-4 w-4 mr-1" />
              <span>Last Updated: April 18, 2025</span>
            </div>
          </div>
          
          <Separator />
          
          <Slideshow slides={lectureSlides} />
          
          {/* Video Embed */}
          <div className="mt-8">
            <h3 className="text-lg font-medium mb-4">Lecture Video</h3>
            <div className="flex justify-center">
              <iframe 
                src="https://share.descript.com/embed/n1V82klaRVZ" 
                width="640" 
                height="360" 
                frameBorder="0" 
                allowFullScreen
                className="rounded-lg shadow-sm"
                title="Thyroid: Molecular and Clinical Applications - Lecture Video"
              />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </MentorshipAccessGate>
  );
};

export default ThyroidMolecularClinical;